/**
 * Applies the effective theme. With the default preference ("Follow house night
 * mode") the current home's night-mode helper drives it live: on → dark, off →
 * light, so an automation switching night mode darkens every open app. Users who
 * can't see the helper (or homes without one) fall back to the system theme.
 *
 * Wall tablets pass their own `home` (their route is /t/:id, not /h/:home, so the URL can't name it and
 * lastHome / homes[0] may be another home) and `followHouse` (always the house, whatever pref is stored).
 *
 * Haunted house (lib/halloween.ts): html[data-haunted] while the home's helper is on (orange accents, app-bar pumpkin);
 * on a wall tablet (`followHouse`) it also forces dark, whatever night mode says, and lets go the moment it's off.
 */
import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { useMatch } from 'react-router-dom';
import { useAccess, useEntity, useHomes, useModel } from '../lib/hooks.js';
import { nightModeId } from '../lib/house.js';
import { useHaunted } from '../lib/halloween.js';
import { getTheme, lastHome, onThemePref, setEffectiveTheme, setPref } from '../lib/prefs.js';

export function ThemeController({ home: fixedHome, followHouse = false, onHouseTheme }: {
  home?: string; followHouse?: boolean;
  /** Called with the house theme whenever the helper's state is known (the Android tablet mirrors it to uimode). */
  onHouseTheme?: (t: 'light' | 'dark') => void;
} = {}): ReactNode {
  const stored = useSyncExternalStore(onThemePref, getTheme);
  const pref = followHouse ? 'house' : stored;
  const homes = useHomes();
  const match = useMatch('/h/:home/*');
  const home = fixedHome ?? match?.params.home ?? lastHome.get() ?? homes[0]?.id ?? '';
  const model = useModel(home);
  const access = useAccess(home);
  const id = pref === 'house' ? nightModeId(model, access) : null;
  const st = useEntity(home, id ?? '');
  const s = id ? st?.s : undefined;
  const known = s === 'on' || s === 'off';
  const ready = !!model && !!access;
  const haunted = useHaunted(home).on;
  const forceDark = followHouse && haunted;
  useEffect(() => {
    const root = document.documentElement;
    if (haunted) root.dataset['haunted'] = '';
    else delete root.dataset['haunted'];
    return () => { delete root.dataset['haunted']; };
  }, [haunted]);
  useEffect(() => {
    if (forceDark) {
      setEffectiveTheme('dark');
      onHouseTheme?.('dark');
      return;
    }
    if (pref !== 'house') {
      setEffectiveTheme(pref);
      return;
    }
    if (known) {
      const t = s === 'on' ? 'dark' : 'light';
      setEffectiveTheme(t);
      // Remembered for first paint next launch (index.html).
      setPref('houseTheme', t);
      onHouseTheme?.(t);
    } else if (ready && !id) {
      setEffectiveTheme('system');
      setPref('houseTheme', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onHouseTheme is a notification, not an input
  }, [pref, known, s, ready, id, forceDark]);
  return null;
}
